import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  SimpleGrid,
  HStack,
  VStack,
  Icon,
  Badge,
  Spinner,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Code,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import axios from "axios";
import { getToken } from "utils/auth";
import config from "config";
import {
  FaHistory,
  FaSearch,
  FaFileDownload,
  FaSync,
  FaUserShield,
  FaUserCheck,
  FaCalendarDay,
  FaLayerGroup,
  FaInfoCircle,
  FaAngleLeft,
  FaAngleRight,
} from "react-icons/fa";

function Logs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Selected log for detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  // Color tokens
  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");
  const filterBg = useColorModeValue("slate.50", "navy.800");
  const headerBg = useColorModeValue("slate.100", "whiteAlpha.50");
  const hoverRowBg = useColorModeValue("slate.50", "whiteAlpha.50");

  const fetchStats = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${config.API_URL}/admin/logs/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching audit stats:", err);
    }
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const params = {
        page,
        limit,
      };
      if (search.trim()) params.q = search.trim();
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      if (roleFilter) params.role = roleFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await axios.get(`${config.API_URL}/admin/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      toast({
        title: "Failed to load audit logs",
        description: err.response?.data?.error || "Could not retrieve logs from server.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, actionFilter, entityFilter, roleFilter, startDate, endDate, toast]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = getToken();
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      if (roleFilter) params.role = roleFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await axios.get(`${config.API_URL}/admin/logs/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Completed",
        description: "Audit logs exported successfully as CSV.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error exporting logs:", err);
      toast({
        title: "Export Failed",
        description: "Could not export audit logs.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleResetFilters = () => {
    setSearch("");
    setActionFilter("");
    setEntityFilter("");
    setRoleFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const getActionBadgeColor = (action) => {
    switch (action?.toUpperCase()) {
      case "CREATE":
        return "green";
      case "UPDATE":
        return "blue";
      case "DELETE":
        return "red";
      case "INVITE":
        return "purple";
      case "ROLE_CHANGE":
        return "orange";
      case "RESTORE":
      case "RESTORE_DB":
        return "yellow";
      case "BACKUP":
        return "teal";
      case "IMPORT":
        return "cyan";
      default:
        return "gray";
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "N/A";
    const d = new Date(isoStr);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getRelativeTime = (isoStr) => {
    if (!isoStr) return "";
    const diff = Math.floor((new Date() - new Date(isoStr)) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    onOpen();
  };

  return (
    <Box pt={{ base: "62px", md: "75px", xl: "88px" }} px={{ base: 4, md: 8 }} pb={8}>
      {/* Header Banner */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        mb={6}
        gap={4}
      >
        <Box>
          <HStack spacing={3} mb={1}>
            <Icon as={FaHistory} w={7} h={7} color="teal.400" />
            <Heading size="lg" color={textColor} fontWeight="bold">
              System Audit Logs
            </Heading>
          </HStack>
          <Text fontSize="sm" color={subtitleColor}>
            Comprehensive audit trail tracking data changes by administrators and moderators
          </Text>
        </Box>

        <HStack spacing={3}>
          <Button
            leftIcon={<FaSync />}
            size="sm"
            variant="outline"
            colorScheme="teal"
            isLoading={loading}
            onClick={() => {
              fetchLogs();
              fetchStats();
            }}
          >
            Refresh
          </Button>
          <Button
            leftIcon={<FaFileDownload />}
            size="sm"
            colorScheme="teal"
            isLoading={exporting}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        </HStack>
      </Flex>

      {/* KPI Stats Cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
        <Box
          bg={cardBg}
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between">
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color={subtitleColor} textTransform="uppercase">
                Total Logged Events
              </Text>
              <Heading size="md" color={textColor} mt={1}>
                {stats?.total_logs ?? total}
              </Heading>
            </Box>
            <Flex
              w={12}
              h={12}
              align="center"
              justify="center"
              borderRadius="xl"
              bg="teal.50"
              _dark={{ bg: "teal.900" }}
              color="teal.400"
            >
              <Icon as={FaLayerGroup} w={5} h={5} />
            </Flex>
          </Flex>
        </Box>

        <Box
          bg={cardBg}
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between">
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color={subtitleColor} textTransform="uppercase">
                Today's Activity
              </Text>
              <Heading size="md" color={textColor} mt={1}>
                {stats?.today_logs ?? 0}
              </Heading>
            </Box>
            <Flex
              w={12}
              h={12}
              align="center"
              justify="center"
              borderRadius="xl"
              bg="blue.50"
              _dark={{ bg: "blue.900" }}
              color="blue.400"
            >
              <Icon as={FaCalendarDay} w={5} h={5} />
            </Flex>
          </Flex>
        </Box>

        <Box
          bg={cardBg}
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between">
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color={subtitleColor} textTransform="uppercase">
                Administrator Actions
              </Text>
              <Heading size="md" color={textColor} mt={1}>
                {stats?.admin_actions ?? 0}
              </Heading>
            </Box>
            <Flex
              w={12}
              h={12}
              align="center"
              justify="center"
              borderRadius="xl"
              bg="red.50"
              _dark={{ bg: "red.900" }}
              color="red.400"
            >
              <Icon as={FaUserShield} w={5} h={5} />
            </Flex>
          </Flex>
        </Box>

        <Box
          bg={cardBg}
          p={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="sm"
        >
          <Flex align="center" justify="space-between">
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color={subtitleColor} textTransform="uppercase">
                Moderator Actions
              </Text>
              <Heading size="md" color={textColor} mt={1}>
                {stats?.moderator_actions ?? 0}
              </Heading>
            </Box>
            <Flex
              w={12}
              h={12}
              align="center"
              justify="center"
              borderRadius="xl"
              bg="purple.50"
              _dark={{ bg: "purple.900" }}
              color="purple.400"
            >
              <Icon as={FaUserCheck} w={5} h={5} />
            </Flex>
          </Flex>
        </Box>
      </SimpleGrid>

      {/* Filter Control Box */}
      <Box
        bg={cardBg}
        p={5}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cardBorder}
        boxShadow="sm"
        mb={6}
      >
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={3}>
          {/* Search Box */}
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search user, entity, details..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              borderRadius="lg"
            />
          </InputGroup>

          {/* Action Filter */}
          <Select
            size="sm"
            placeholder="All Actions"
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            borderRadius="lg"
          >
            <option value="CREATE">CREATE (Addition)</option>
            <option value="UPDATE">UPDATE (Edit)</option>
            <option value="DELETE">DELETE (Removal)</option>
            <option value="ROLE_CHANGE">ROLE_CHANGE (Permission)</option>
            <option value="INVITE">INVITE (User Invitation)</option>
            <option value="RESTORE">RESTORE (Entity Reactivation)</option>
            <option value="BACKUP">BACKUP (Database Export)</option>
            <option value="RESTORE_DB">RESTORE_DB (Database Import)</option>
            <option value="IMPORT">IMPORT (CSV Batch)</option>
          </Select>

          {/* Entity Type Filter */}
          <Select
            size="sm"
            placeholder="All Entities"
            value={entityFilter}
            onChange={(e) => {
              setEntityFilter(e.target.value);
              setPage(1);
            }}
            borderRadius="lg"
          >
            <option value="individual">Individual / Patient</option>
            <option value="family">Family</option>
            <option value="user">User Account</option>
            <option value="invitation">Invitation</option>
            <option value="database">Database</option>
            <option value="post">Forum Post</option>
            <option value="comment">Forum Comment</option>
          </Select>

          {/* Role Filter */}
          <Select
            size="sm"
            placeholder="All Roles"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            borderRadius="lg"
          >
            <option value="admin">Administrator</option>
            <option value="moderator">Moderator</option>
          </Select>
        </SimpleGrid>

        {/* Date Filters & Reset */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "stretch", sm: "center" }}
          gap={3}
          pt={2}
          borderTop="1px solid"
          borderColor={cardBorder}
        >
          <HStack spacing={2} wrap="wrap">
            <Text fontSize="xs" color={subtitleColor} fontWeight="medium">
              Date Range:
            </Text>
            <Input
              type="date"
              size="xs"
              w="140px"
              borderRadius="md"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
            />
            <Text fontSize="xs" color={subtitleColor}>
              to
            </Text>
            <Input
              type="date"
              size="xs"
              w="140px"
              borderRadius="md"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
            />
          </HStack>

          <Button
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={handleResetFilters}
            alignSelf={{ base: "flex-end", sm: "auto" }}
          >
            Reset Filters
          </Button>
        </Flex>
      </Box>

      {/* Logs Table Container */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={cardBorder}
        boxShadow="sm"
        overflow="hidden"
      >
        {loading ? (
          <Flex justify="center" align="center" py={20}>
            <VStack spacing={3}>
              <Spinner size="xl" color="teal.400" thickness="3px" />
              <Text fontSize="sm" color={subtitleColor}>
                Loading audit trail...
              </Text>
            </VStack>
          </Flex>
        ) : logs.length === 0 ? (
          <Flex justify="center" align="center" py={16} direction="column" gap={3}>
            <Icon as={FaHistory} w={10} h={10} color="gray.300" />
            <Text fontSize="md" fontWeight="medium" color={textColor}>
              No audit logs found
            </Text>
            <Text fontSize="xs" color={subtitleColor}>
              Try adjusting your search criteria or date filters.
            </Text>
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={headerBg}>
                <Tr>
                  <Th color={subtitleColor} py={3}>Timestamp</Th>
                  <Th color={subtitleColor} py={3}>Actor</Th>
                  <Th color={subtitleColor} py={3}>Action</Th>
                  <Th color={subtitleColor} py={3}>Target Entity</Th>
                  <Th color={subtitleColor} py={3}>Details</Th>
                  <Th color={subtitleColor} py={3}>IP Address</Th>
                  <Th color={subtitleColor} py={3} textAlign="right">Inspect</Th>
                </Tr>
              </Thead>
              <Tbody>
                {logs.map((log) => (
                  <Tr
                    key={log.id}
                    _hover={{ bg: hoverRowBg }}
                    transition="background-color 0.15s"
                  >
                    {/* Timestamp */}
                    <Td py={3} whiteSpace="nowrap">
                      <Tooltip label={getRelativeTime(log.created_at)} placement="top">
                        <Text fontSize="xs" fontWeight="semibold" color={textColor}>
                          {formatDate(log.created_at)}
                        </Text>
                      </Tooltip>
                    </Td>

                    {/* Actor */}
                    <Td py={3}>
                      <VStack align="flex-start" spacing={0}>
                        <Text fontSize="xs" fontWeight="medium" color={textColor}>
                          {log.user_email || `User #${log.user_id}`}
                        </Text>
                        <Badge
                          size="xs"
                          fontSize="9px"
                          px={1.5}
                          borderRadius="full"
                          colorScheme={log.user_role === "admin" ? "red" : "purple"}
                        >
                          {log.user_role || "system"}
                        </Badge>
                      </VStack>
                    </Td>

                    {/* Action */}
                    <Td py={3}>
                      <Badge
                        px={2}
                        py={0.5}
                        borderRadius="md"
                        fontSize="10px"
                        fontWeight="bold"
                        colorScheme={getActionBadgeColor(log.action)}
                      >
                        {log.action}
                      </Badge>
                    </Td>

                    {/* Target Entity */}
                    <Td py={3} whiteSpace="nowrap">
                      <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="md">
                        <TagLabel fontSize="xs">
                          {log.entity_type} : <strong>{log.entity_id || "-"}</strong>
                        </TagLabel>
                      </Tag>
                    </Td>

                    {/* Details */}
                    <Td py={3} maxW="300px">
                      <Tooltip label={log.details} placement="top-start" hasArrow>
                        <Text fontSize="xs" color={textColor} isTruncated>
                          {log.details}
                        </Text>
                      </Tooltip>
                    </Td>

                    {/* IP Address */}
                    <Td py={3} whiteSpace="nowrap">
                      <Code fontSize="10px" px={1.5} py={0.5} borderRadius="md">
                        {log.ip_address || "127.0.0.1"}
                      </Code>
                    </Td>

                    {/* Inspect Button */}
                    <Td py={3} textAlign="right">
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="teal"
                        onClick={() => openLogDetail(log)}
                        leftIcon={<FaInfoCircle />}
                      >
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Pagination Footer */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align="center"
          px={5}
          py={3}
          borderTop="1px solid"
          borderColor={cardBorder}
          gap={3}
        >
          <Text fontSize="xs" color={subtitleColor}>
            Showing {logs.length > 0 ? (page - 1) * limit + 1 : 0} to{" "}
            {Math.min(page * limit, total)} of {total} events
          </Text>

          <HStack spacing={2}>
            <Button
              size="xs"
              variant="outline"
              leftIcon={<FaAngleLeft />}
              isDisabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Text fontSize="xs" fontWeight="semibold" px={2} color={textColor}>
              Page {page} of {totalPages}
            </Text>
            <Button
              size="xs"
              variant="outline"
              rightIcon={<FaAngleRight />}
              isDisabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Log Detail Modal */}
      {selectedLog && (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
          <ModalOverlay backdropFilter="blur(3px)" />
          <ModalContent bg={cardBg} borderRadius="xl" borderColor={cardBorder} borderWidth="1px">
            <ModalHeader borderBottom="1px solid" borderColor={cardBorder} pb={3}>
              <HStack spacing={2}>
                <Icon as={FaInfoCircle} color="teal.400" />
                <Text fontSize="md" color={textColor}>
                  Audit Log Event #{selectedLog.id}
                </Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={4}>
              <VStack align="stretch" spacing={3}>
                <Flex justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color={subtitleColor}>
                    Action:
                  </Text>
                  <Badge colorScheme={getActionBadgeColor(selectedLog.action)} px={2} borderRadius="md">
                    {selectedLog.action}
                  </Badge>
                </Flex>

                <Flex justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color={subtitleColor}>
                    Timestamp:
                  </Text>
                  <Text fontSize="xs" color={textColor}>
                    {formatDate(selectedLog.created_at)} ({getRelativeTime(selectedLog.created_at)})
                  </Text>
                </Flex>

                <Flex justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color={subtitleColor}>
                    Actor:
                  </Text>
                  <HStack spacing={1}>
                    <Text fontSize="xs" fontWeight="medium" color={textColor}>
                      {selectedLog.user_email || "N/A"}
                    </Text>
                    <Badge size="xs" colorScheme={selectedLog.user_role === "admin" ? "red" : "purple"}>
                      {selectedLog.user_role}
                    </Badge>
                  </HStack>
                </Flex>

                <Flex justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color={subtitleColor}>
                    Target Entity:
                  </Text>
                  <Tag size="sm" variant="subtle" colorScheme="gray">
                    {selectedLog.entity_type} : {selectedLog.entity_id}
                  </Tag>
                </Flex>

                <Flex justify="space-between">
                  <Text fontSize="xs" fontWeight="bold" color={subtitleColor}>
                    Client IP Address:
                  </Text>
                  <Code fontSize="xs">{selectedLog.ip_address || "127.0.0.1"}</Code>
                </Flex>

                <Box pt={2}>
                  <Text fontSize="xs" fontWeight="bold" color={subtitleColor} mb={1}>
                    Details & Context:
                  </Text>
                  <Box
                    p={3}
                    bg={filterBg}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={cardBorder}
                  >
                    <Text fontSize="xs" color={textColor} whiteSpace="pre-wrap">
                      {selectedLog.details || "No additional description available."}
                    </Text>
                  </Box>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor={cardBorder} pt={3}>
              <Button size="sm" colorScheme="teal" onClick={onClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default Logs;
